// src/context/QueryModalContext.tsx
// Ownership of the Query window.
//
// The Navbar and the Footer are siblings under the router, not parent/child, so
// neither can receive the other's open handler as a prop without threading it
// through App -> AppShell -> Routes -> page -> Footer. This provider is the
// nearest common parent, and it is the smallest thing that solves the problem:
// two `useState` lines, no store, no library.
//
// It deliberately mirrors src/context/ThemeContext.tsx (context + provider +
// hook that throws outside the provider) so the codebase has one way of sharing
// app-level state.
//
// The single <QueryModal> instance is rendered here rather than by each caller.
// ModalBox portals it to document.body, so where it sits in the tree does not
// affect layout or stacking -- mounting it in two places would have produced
// two dialogs competing for the same focus trap and scroll lock.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import QueryModal from '../components/Query/QueryModal';

interface QueryModalContextValue {
  /** Whether the Query window is open. Read only if a surface must react to it. */
  isQueryModalOpen: boolean;
  /** Opens the window. Safe to call from any surface. */
  openQueryModal: () => void;
  /** Closes the window. */
  closeQueryModal: () => void;
}

const QueryModalContext = createContext<QueryModalContextValue | undefined>(undefined);

export const QueryModalProvider = ({ children }: { children: ReactNode }) => {
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);

  // Stable identities: these are passed to the memoised value below, and the
  // modal's own close paths are wired to them directly.
  const openQueryModal = useCallback(() => setIsQueryModalOpen(true), []);
  const closeQueryModal = useCallback(() => setIsQueryModalOpen(false), []);

  const value = useMemo(
    () => ({ isQueryModalOpen, openQueryModal, closeQueryModal }),
    [isQueryModalOpen, openQueryModal, closeQueryModal]
  );

  return (
    <QueryModalContext.Provider value={value}>
      {children}
      <QueryModal isOpen={isQueryModalOpen} onClose={closeQueryModal} />
    </QueryModalContext.Provider>
  );
};

// Custom hook
export const useQueryModal = () => {
  const context = useContext(QueryModalContext);
  if (!context) throw new Error('useQueryModal must be used within QueryModalProvider');
  return context;
};
